import styled from "@emotion/styled";
import { Col } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import React, { FC, ReactNode } from "react";

export type EmptyStateIllustration = "workStation" | "catPlaysFolder";

interface IllustrationLayer {
  name: string;
  src: string;
  inset: string;
}

interface IllustrationSpec {
  height: string;
  padding: string;
  frameWidth: string;
  frameHeight: string;
  layers: IllustrationLayer[];
}

const ILLUSTRATIONS: Record<EmptyStateIllustration, IllustrationSpec> = {
  workStation: {
    height: "194px",
    padding: "13.605px",
    frameWidth: "239.79px",
    frameHeight: "166.662px",
    layers: [
      { name: "table", src: "/img/empty-state-workstation-table.svg", inset: "23.08% 8.47% 0 0" },
      {
        name: "coffee",
        src: "/img/empty-state-workstation-coffee.svg",
        inset: "9.28% 37.94% 77% 54.8%",
      },
      {
        name: "laptop",
        src: "/img/empty-state-workstation-laptop.svg",
        inset: "0 27.34% 76.93% 27.58%",
      },
      { name: "chair", src: "/img/empty-state-workstation-chair.svg", inset: "15.75% 0 0 50.46%" },
    ],
  },
  catPlaysFolder: {
    height: "194px",
    padding: "13.605px",
    frameWidth: "279.6px",
    frameHeight: "166.662px",
    layers: [{ name: "cat", src: "/img/empty-state-cat-plays-folder.svg", inset: "0" }],
  },
};

// Fixed frame geometry from Figma frame 1.1a keeps the artwork's aspect ratio in any column width.
const IllustrationBox = styled.div<{ boxHeight: string; boxPadding: string }>(
  ({ boxHeight, boxPadding }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    height: boxHeight,
    padding: boxPadding,
  })
);

const IllustrationFrame = styled.div<{ frameWidth: string; frameHeight: string }>(
  ({ frameWidth, frameHeight }) => ({
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
    width: frameWidth,
    height: frameHeight,
  })
);

const IllustrationLayer = styled.div<{ layerInset: string }>(({ layerInset }) => ({
  position: "absolute",
  inset: layerInset,
}));

const IllustrationLayerImage = styled.img({
  display: "block",
  width: "100%",
  height: "100%",
});

const Illustration: FC<{ illustration: EmptyStateIllustration }> = ({ illustration }) => {
  const { height, padding, frameWidth, frameHeight, layers } = ILLUSTRATIONS[illustration];

  return (
    <IllustrationBox boxHeight={height} boxPadding={padding} aria-hidden="true">
      <IllustrationFrame frameWidth={frameWidth} frameHeight={frameHeight}>
        {layers.map((layer) => (
          <IllustrationLayer key={layer.name} layerInset={layer.inset}>
            <IllustrationLayerImage src={layer.src} alt="" />
          </IllustrationLayer>
        ))}
      </IllustrationFrame>
    </IllustrationBox>
  );
};

interface EmptyStateProps {
  illustration: EmptyStateIllustration;
  heading: string;
  description: string;
  action?: ReactNode;
  /** Announces the state to screen readers, for states the person did not ask for (e.g. a failed load). */
  isAlert?: boolean;
  dataTestId?: string;
}

export const EmptyState: FC<EmptyStateProps> = ({
  illustration,
  heading,
  description,
  action,
  isAlert = false,
  dataTestId,
}) => (
  <Col
    width="100%"
    alignItems="center"
    gridGap="S300"
    padding={["S700", 0]}
    role={isAlert ? "alert" : undefined}
    data-testid={dataTestId}
  >
    <Illustration illustration={illustration} />
    <Text fontSize="T300" fontWeight="bold" color="neutral100">
      {heading}
    </Text>
    <Text fontSize="T100" color="neutral70" textAlign="center">
      {description}
    </Text>
    {action}
  </Col>
);
